import React, { useState, useCallback, useRef, useEffect } from 'react'
import { ClassItem, ImageItem, IndexStatus } from '@/types/catalog'
import { Category, ClassThumbnail } from '@/types'
import MobileCatalogClassList from './MobileCatalogClassList'
import MobileCatalogImageGrid from './MobileCatalogImageGrid'
import MobileCatalogImagePreview from './MobileCatalogImagePreview'
import ClassEditDialog from '@/components/ClassEditDialog'
import CategoryManagementDialog from '@/components/CategoryManagementDialog'
import ThumbnailSelectionDialog from '@/components/ThumbnailSelectionDialog'
import { Loader2 } from 'lucide-react'

type ViewMode = 'classList' | 'classDetail'

interface MobileCatalogViewProps {
  // Data
  classes: ClassItem[]
  images: ImageItem[]
  selectedClass: string | null
  indexStatus: IndexStatus
  isAdmin: boolean
  loading: boolean
  isUploading: boolean
  uploadProgress: { current: number; total: number }

  // Category context
  categories: Record<string, Category>
  getClassCategory: (classId: string) => Category | null
  getClassThumbnail: (classId: string) => ClassThumbnail | null

  // Filter/sort
  classFilter: string
  sortBy: 'lastModified' | 'imageCount' | 'name'
  sortOrder: 'asc' | 'desc'
  sortedAndFilteredClasses: ClassItem[]

  // Callbacks
  onSelectClass: (classId: string | null) => void
  onCreateClass: (name: string) => Promise<void>
  onDeleteClass: (classId: string) => Promise<void>
  onUploadFiles: (files: File[]) => Promise<void>
  onDeleteImage: (imageId: string) => Promise<void>
  onSetThumbnail: (classId: string, fileName: string) => Promise<void>
  onRefresh: () => Promise<void>
  onFilterChange: (filter: string) => void
  onSortChange: (sortBy: 'lastModified' | 'imageCount' | 'name') => void
  onToggleSortOrder: () => void
  onImportZip: (file: File) => void
  onCategoryChange: () => void
  onTagUpdate: () => void

  // Index operations (admin only)
  onInitializeEmbeddings?: () => Promise<void>
  onForceRebuild?: () => Promise<void>
  isIndexRebuilding?: boolean
}

const MobileCatalogView: React.FC<MobileCatalogViewProps> = ({
  classes,
  images,
  selectedClass,
  indexStatus,
  isAdmin,
  loading,
  isUploading,
  uploadProgress,
  categories,
  getClassCategory,
  getClassThumbnail,
  classFilter,
  sortBy,
  sortOrder,
  sortedAndFilteredClasses,
  onSelectClass,
  onCreateClass,
  onDeleteClass,
  onUploadFiles,
  onDeleteImage,
  onSetThumbnail,
  onRefresh,
  onFilterChange,
  onSortChange,
  onToggleSortOrder,
  onImportZip,
  onCategoryChange,
  onTagUpdate,
  onInitializeEmbeddings,
  onForceRebuild,
  isIndexRebuilding = false,
}) => {
  // View state - start with classDetail if a class is already selected (from URL)
  const [viewMode, setViewMode] = useState<ViewMode>(() =>
    selectedClass ? 'classDetail' : 'classList'
  )

  // Image preview state
  const [previewImage, setPreviewImage] = useState<{
    index: number
    id: string
    url: string
  } | null>(null)

  // Dialog states
  const [showCreateClass, setShowCreateClass] = useState(false)
  const [showEditClass, setShowEditClass] = useState<string | null>(null)
  const [showCategoryManagement, setShowCategoryManagement] = useState(false)
  const [showThumbnailSelection, setShowThumbnailSelection] = useState<{
    classId: string
    className: string
  } | null>(null)

  // New class form
  const [newClassName, setNewClassName] = useState('')
  const [isCreating, setIsCreating] = useState(false)

  // File input refs
  const fileInputRef = useRef<HTMLInputElement>(null)
  const zipInputRef = useRef<HTMLInputElement>(null)

  // Get selected class data
  const selectedClassData = classes.find(c => c.id === selectedClass)

  // Reset to classList when selectedClass becomes null (e.g., after class deletion)
  useEffect(() => {
    if (!selectedClass && viewMode === 'classDetail') {
      setViewMode('classList')
    }
  }, [selectedClass, viewMode])

  // Handle class selection
  const handleSelectClass = useCallback((classId: string) => {
    onSelectClass(classId)
    setViewMode('classDetail')
  }, [onSelectClass])

  // Handle back to list
  const handleBackToList = useCallback(() => {
    setViewMode('classList')
    onSelectClass(null)
  }, [onSelectClass])

  // Handle create class
  const handleCreateClass = useCallback(async () => {
    if (!newClassName.trim() || isCreating) return

    setIsCreating(true)
    try {
      await onCreateClass(newClassName.trim())
      setNewClassName('')
      setShowCreateClass(false)
    } finally {
      setIsCreating(false)
    }
  }, [newClassName, isCreating, onCreateClass])

  // Handle file upload
  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    const imageFiles = Array.from(files).filter(
      file => file.type.startsWith('image/') ||
              file.name.toLowerCase().endsWith('.heic') ||
              file.name.toLowerCase().endsWith('.heif')
    )

    if (imageFiles.length > 0) {
      await onUploadFiles(imageFiles)
    }

    // Reset input
    e.target.value = ''
  }, [onUploadFiles])

  // Handle ZIP import
  const handleZipSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.name.toLowerCase().endsWith('.zip')) {
      alert('Please select a ZIP file')
      return
    }

    onImportZip(file)
    e.target.value = ''
  }, [onImportZip])

  // Handle image tap for preview
  const handleImageTap = useCallback((imageId: string) => {
    const index = images.findIndex(img => img.id === imageId)
    if (index !== -1) {
      const image = images[index]
      setPreviewImage({ index, id: image.id, url: image.imageUrl })
    }
  }, [images])

  // Handle image navigation in preview
  const handleNavigateImage = useCallback((direction: 'prev' | 'next') => {
    if (!previewImage || images.length === 0) return

    let newIndex: number
    if (direction === 'prev') {
      newIndex = previewImage.index > 0 ? previewImage.index - 1 : images.length - 1
    } else {
      newIndex = previewImage.index < images.length - 1 ? previewImage.index + 1 : 0
    }

    const newImage = images[newIndex]
    setPreviewImage({ index: newIndex, id: newImage.id, url: newImage.imageUrl })
  }, [previewImage, images])

  // Handle set thumbnail from preview
  const handleSetThumbnailFromPreview = useCallback(async () => {
    if (!previewImage || !selectedClass) return

    const image = images[previewImage.index]
    if (image) {
      await onSetThumbnail(selectedClass, image.fileName)
    }
  }, [previewImage, selectedClass, images, onSetThumbnail])

  // Handle delete from preview
  const handleDeleteFromPreview = useCallback(async () => {
    if (!previewImage) return

    if (!confirm('Delete this image?')) return

    await onDeleteImage(previewImage.id)

    // Close preview or navigate to next
    if (images.length <= 1) {
      setPreviewImage(null)
    } else {
      // Navigate to next (or prev if at end)
      const newIndex = previewImage.index < images.length - 1
        ? previewImage.index
        : previewImage.index - 1
      if (newIndex >= 0 && newIndex < images.length - 1) {
        const newImage = images[newIndex === previewImage.index ? newIndex + 1 : newIndex]
        if (newImage) {
          setPreviewImage({ index: newIndex, id: newImage.id, url: newImage.imageUrl })
        }
      } else {
        setPreviewImage(null)
      }
    }
  }, [previewImage, images, onDeleteImage])

  // Get current thumbnail filename
  const currentThumbnailFileName = selectedClass
    ? getClassThumbnail(selectedClass)?.path
    : null

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2 text-primary-500" />
          <p className="text-gray-500">Loading catalog...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,.heic,.heif"
        multiple
        onChange={handleFileSelect}
        className="hidden"
      />
      <input
        ref={zipInputRef}
        type="file"
        accept=".zip"
        onChange={handleZipSelect}
        className="hidden"
      />

      {/* Main Content */}
      {viewMode === 'classList' && (
        <MobileCatalogClassList
          classes={sortedAndFilteredClasses}
          indexStatus={indexStatus}
          isAdmin={isAdmin}
          classFilter={classFilter}
          sortBy={sortBy}
          sortOrder={sortOrder}
          getClassCategory={getClassCategory}
          getClassThumbnail={getClassThumbnail}
          onSelectClass={handleSelectClass}
          onEditClass={setShowEditClass}
          onDeleteClass={onDeleteClass}
          onFilterChange={onFilterChange}
          onSortChange={onSortChange}
          onToggleSortOrder={onToggleSortOrder}
          onCreateClass={() => setShowCreateClass(true)}
          onImportZip={() => zipInputRef.current?.click()}
          onRefresh={onRefresh}
          onManageCategories={() => setShowCategoryManagement(true)}
        />
      )}

      {viewMode === 'classDetail' && selectedClassData && (
        <MobileCatalogImageGrid
          classData={selectedClassData}
          images={images}
          isAdmin={isAdmin}
          isUploading={isUploading}
          uploadProgress={uploadProgress}
          thumbnailFileName={currentThumbnailFileName}
          category={selectedClass ? getClassCategory(selectedClass) : null}
          onBack={handleBackToList}
          onImageTap={handleImageTap}
          onUpload={() => fileInputRef.current?.click()}
          onEditClass={() => selectedClass && setShowEditClass(selectedClass)}
          onSelectThumbnail={() => selectedClassData && setShowThumbnailSelection({
            classId: selectedClassData.id,
            className: selectedClassData.displayName
          })}
        />
      )}

      {/* Image Preview Modal */}
      {previewImage && (
        <MobileCatalogImagePreview
          imageUrl={previewImage.url}
          imageIndex={previewImage.index}
          totalImages={images.length}
          isAdmin={isAdmin}
          isThumbnail={currentThumbnailFileName === images[previewImage.index]?.fileName}
          onClose={() => setPreviewImage(null)}
          onPrev={() => handleNavigateImage('prev')}
          onNext={() => handleNavigateImage('next')}
          onSetThumbnail={handleSetThumbnailFromPreview}
          onDelete={handleDeleteFromPreview}
        />
      )}

      {/* Upload Progress Overlay */}
      {isUploading && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center">
          <div className="bg-white rounded-2xl p-6 mx-4 max-w-sm w-full text-center">
            <Loader2 className="h-12 w-12 text-primary-600 animate-spin mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Uploading Images
            </h3>
            <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
              <div
                className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                style={{
                  width: uploadProgress.total > 0
                    ? `${(uploadProgress.current / uploadProgress.total) * 100}%`
                    : '0%'
                }}
              />
            </div>
            <p className="text-sm text-gray-500">
              {uploadProgress.current} of {uploadProgress.total} files
            </p>
          </div>
        </div>
      )}

      {/* Create Class Dialog */}
      {showCreateClass && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-50"
            onClick={() => setShowCreateClass(false)}
          />
          <div className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-2xl shadow-2xl animate-in slide-in-from-bottom duration-300 safe-area-bottom">
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-10 h-1 bg-gray-300 rounded-full" />
            </div>
            <div className="p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Class</h3>
              <input
                type="text"
                value={newClassName}
                onChange={(e) => setNewClassName(e.target.value)}
                placeholder="Enter class name..."
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                autoFocus
              />
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => setShowCreateClass(false)}
                  className="flex-1 py-3 rounded-xl font-medium bg-gray-100 text-gray-700 active:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateClass}
                  disabled={!newClassName.trim() || isCreating}
                  className="flex-1 py-3 rounded-xl font-medium bg-primary-600 text-white active:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCreating ? 'Creating...' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Edit Class Dialog */}
      {showEditClass && (
        <ClassEditDialog
          classId={showEditClass}
          isOpen={true}
          onClose={() => setShowEditClass(null)}
          onRenamed={(oldId, newId) => {
            setShowEditClass(null)
            // Update selection to new class ID
            if (selectedClass === oldId) {
              onSelectClass(newId)
            }
            onCategoryChange()
            onRefresh()
          }}
          onDeleted={(deletedId) => {
            setShowEditClass(null)
            if (viewMode === 'classDetail') {
              handleBackToList()
            }
            onCategoryChange()
            onRefresh()
          }}
        />
      )}

      {/* Category Management Dialog */}
      {showCategoryManagement && (
        <CategoryManagementDialog
          isOpen={true}
          onClose={() => setShowCategoryManagement(false)}
        />
      )}

      {/* Thumbnail Selection Dialog */}
      {showThumbnailSelection && (
        <ThumbnailSelectionDialog
          isOpen={true}
          classId={showThumbnailSelection.classId}
          className={showThumbnailSelection.className}
          onClose={() => {
            setShowThumbnailSelection(null)
            onRefresh()
          }}
        />
      )}
    </div>
  )
}

export default MobileCatalogView
